import React, { useState } from 'react';
import './JsonTreeViewer.css';

interface SchemaInfo {
  nodes: Set<string>;
  edges: Set<string>;
  nodeProperties: Map<string, Set<string>>;
  edgeProperties: Map<string, Set<string>>;
}

interface JsonTreeViewerProps {
  data: any;
  name?: string;
  isRoot?: boolean;
  schemaInfo?: SchemaInfo | null;
  path?: string[];
}

const JsonTreeViewer: React.FC<JsonTreeViewerProps> = ({
  data,
  name,
  isRoot = false,
  schemaInfo = null,
  path = []
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Determine if this key should show a warning
  const getWarning = (key: string, parentPath: string[]): string | null => {
    if (!schemaInfo) return null;

    // parentPath is the path of the PARENT, not including the current key
    // So we need to check what level we're at by looking at parentPath length

    // Level 1: Top-level keys like "node_descriptions", "edge_descriptions", "foo", etc.
    // parentPath.length === 0, so no validation needed
    if (parentPath.length === 0) {
      return null;
    }

    // Level 2: Keys inside node_descriptions or edge_descriptions (the node/edge names)
    // parentPath.length === 1, parentPath[0] could be "node_descriptions" or "edge_descriptions"
    if (parentPath.length === 1) {
      if (parentPath[0] === 'node_descriptions') {
        // key is a node label
        if (!schemaInfo.nodes.has(key)) {
          return `Node "${key}" does not exist in schema`;
        }
      } else if (parentPath[0] === 'edge_descriptions') {
        // key is an edge type
        if (!schemaInfo.edges.has(key)) {
          return `Edge "${key}" does not exist in schema`;
        }
      }
      return null;
    }

    // Level 3: Keys inside node_descriptions > node_name or edge_descriptions > edge_name (the properties)
    // parentPath.length === 2
    if (parentPath.length === 2) {
      if (parentPath[0] === 'node_descriptions') {
        const nodeLabel = parentPath[1];
        // Skip __description as it's always valid
        if (key === '__description') {
          return null;
        }
        // Only check property if the node exists in schema
        if (schemaInfo.nodes.has(nodeLabel)) {
          const nodeProps = schemaInfo.nodeProperties.get(nodeLabel);
          if (nodeProps && !nodeProps.has(key)) {
            return `Property "${key}" does not exist for node "${nodeLabel}"`;
          }
        }
      } else if (parentPath[0] === 'edge_descriptions') {
        const edgeType = parentPath[1];
        // Skip __description as it's always valid
        if (key === '__description') {
          return null;
        }
        // Only check property if the edge exists in schema
        if (schemaInfo.edges.has(edgeType)) {
          const edgeProps = schemaInfo.edgeProperties.get(edgeType);
          if (edgeProps && !edgeProps.has(key)) {
            return `Property "${key}" does not exist for edge "${edgeType}"`;
          }
        }
      }
      return null;
    }

    // Deeper levels: no validation
    return null;
  };

  const getValueType = (value: any): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const renderArrayItem = (value: any, index: number): React.JSX.Element => {
    const type = getValueType(value);

    if (type === 'object' && value !== null) {
      return (
        <JsonTreeViewer
          data={value}
          schemaInfo={schemaInfo}
          path={path}
        />
      );
    }

    if (type === 'array') {
      return (
        <JsonTreeViewer
          data={value}
          schemaInfo={schemaInfo}
          path={path}
        />
      );
    }

    // Primitive values in arrays
    return (
      <span className={`json-tree-value json-tree-value-${type}`}>
        {type === 'string' ? `"${value}"` : String(value)}
      </span>
    );
  };

  if (data === null || data === undefined) {
    return (
      <div className="json-tree-leaf">
        {name && <span className="json-tree-key">{name}: </span>}
        <span className="json-tree-value json-tree-value-null">null</span>
      </div>
    );
  }

  const type = getValueType(data);

  // Handle primitive types at root level
  if (type !== 'object' && type !== 'array') {
    return (
      <div className="json-tree-leaf">
        {name && <span className="json-tree-key">{name}: </span>}
        <span className={`json-tree-value json-tree-value-${type}`}>
          {type === 'string' ? `"${data}"` : String(data)}
        </span>
      </div>
    );
  }

  const isArray = Array.isArray(data);
  const isEmpty = isArray ? data.length === 0 : Object.keys(data).length === 0;

  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';

  // Get warning for the current node's name (if it has one)
  // The `path` prop is the FULL path to this node, including its own name
  // So to validate this node's name against its parent, we need to use path WITHOUT the last element
  const parentPath = name && path.length > 0 ? path.slice(0, -1) : [];
  const currentWarning = name ? getWarning(name, parentPath) : null;

  return (
    <div className={`json-tree-node ${isRoot ? 'json-tree-root' : ''}`}>
      <div
        className="json-tree-node-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="json-tree-toggle">
          {isEmpty ? '⊙' : isExpanded ? '▼' : '▶'}
        </span>
        {name && (
          <>
            <span className="json-tree-key">{name}: </span>
            {currentWarning && (
              <span className="json-tree-warning" title={currentWarning}>
                ⚠️
              </span>
            )}
          </>
        )}
        <span className="json-tree-bracket">{openBracket}</span>
        {!isExpanded && (
          <>
            <span className="json-tree-preview">
              {isEmpty ? '' : ` ${isArray ? data.length : Object.keys(data).length} ${isArray ? 'items' : 'keys'} `}
            </span>
            <span className="json-tree-bracket">{closeBracket}</span>
          </>
        )}
      </div>

      {isExpanded && (
        <div className="json-tree-children">
          {isEmpty ? (
            <div className="json-tree-empty">
              {isArray ? 'Empty array' : 'Empty object'}
            </div>
          ) : isArray ? (
            data.map((item: any, index: number) => (
              <div key={index} className="json-tree-item">
                <span className="json-tree-array-index">[{index}]</span>
                {renderArrayItem(item, index)}
              </div>
            ))
          ) : (
            Object.entries(data).map(([key, value]) => {
              const type = getValueType(value);
              const warning = getWarning(key, path);
              const newPath = [...path, key];

              if (type === 'object' && value !== null) {
                return (
                  <div key={key} className="json-tree-item">
                    <JsonTreeViewer
                      data={value}
                      name={key}
                      schemaInfo={schemaInfo}
                      path={newPath}
                    />
                  </div>
                );
              }

              if (type === 'array') {
                return (
                  <div key={key} className="json-tree-item">
                    <JsonTreeViewer
                      data={value}
                      name={key}
                      schemaInfo={schemaInfo}
                      path={newPath}
                    />
                  </div>
                );
              }

              // Primitive values
              return (
                <div key={key} className="json-tree-item">
                  <div className="json-tree-leaf">
                    <span className="json-tree-key">{key}: </span>
                    {warning && (
                      <span className="json-tree-warning" title={warning}>
                        ⚠️
                      </span>
                    )}
                    <span className={`json-tree-value json-tree-value-${type}`}>
                      {type === 'string' ? `"${value}"` : String(value)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
          <div className="json-tree-closing-bracket">
            <span className="json-tree-bracket">{closeBracket}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default JsonTreeViewer;
